import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";

import type { DropdownOption } from "../Dropdown";
import { getInitialHighlightedIndex, getNextEnabledIndex } from "../utils/dropdownKeyboardUtils";

type UseDropdownKeyboardOptions = {
  options: DropdownOption[];
  disabled?: boolean;
  selectedValue?: string | number;
  onSelectOption: (option: DropdownOption) => void;
  containerRef: RefObject<HTMLDivElement | null>;
};

export function useDropdownKeyboard({
  options,
  disabled,
  selectedValue,
  onSelectOption,
  containerRef,
}: UseDropdownKeyboardOptions) {
  const [collapsed, setCollapsed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const menuId = useId().replace(/:/g, "");
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const closeMenu = useCallback(() => {
    setCollapsed(false);
    setHighlightedIndex(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setCollapsed(true);
    setHighlightedIndex(getInitialHighlightedIndex(options, selectedValue));
  }, [disabled, options, selectedValue]);

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (disabled) return;
      if (event.currentTarget.matches(":focus-visible")) {
        openMenu();
      }
    },
    [disabled, openMenu],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const relatedTarget = event.relatedTarget as Node | null;
      if (relatedTarget && containerRef.current?.contains(relatedTarget)) {
        return;
      }
      closeMenu();
    },
    [closeMenu, containerRef],
  );

  const moveHighlight = useCallback(
    (direction: 1 | -1) => {
      if (disabled) return;

      if (!collapsed) {
        openMenu();
        return;
      }

      setHighlightedIndex((current) => {
        const start = current < 0 ? (direction === 1 ? -1 : 0) : current;
        return getNextEnabledIndex(options, start, direction);
      });
    },
    [collapsed, disabled, openMenu, options],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (disabled) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          moveHighlight(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          moveHighlight(-1);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (collapsed) {
            if (highlightedIndex >= 0 && !options[highlightedIndex]?.disabled) {
              onSelectOption(options[highlightedIndex]);
              closeMenu();
            }
          } else {
            openMenu();
          }
          break;
        case "Escape":
          event.preventDefault();
          closeMenu();
          break;
        case "Tab":
          closeMenu();
          break;
        default:
          break;
      }
    },
    [closeMenu, collapsed, disabled, highlightedIndex, moveHighlight, onSelectOption, openMenu, options],
  );

  const toggleDropdown = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      if (event?.stopPropagation) event.stopPropagation();
      if (disabled) return;
      if (collapsed) {
        closeMenu();
      } else {
        openMenu();
      }
    },
    [collapsed, closeMenu, disabled, openMenu],
  );

  useEffect(() => {
    if (!collapsed || highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [collapsed, highlightedIndex]);

  const activeDescendantId =
    collapsed && highlightedIndex >= 0 ? `${menuId}-option-${highlightedIndex}` : undefined;

  const triggerAriaProps = {
    "aria-expanded": collapsed,
    "aria-haspopup": "listbox" as const,
    "aria-controls": collapsed ? menuId : undefined,
    "aria-activedescendant": activeDescendantId,
  };

  return {
    collapsed,
    highlightedIndex,
    menuId,
    optionRefs,
    closeMenu,
    toggleDropdown,
    handleFocus,
    handleBlur,
    handleKeyDown,
    triggerAriaProps,
  };
}
